import React, { useContext, useEffect } from "react";
import { Breadcrumb } from "react-bootstrap";
import { NavLink, useHistory } from "react-router-dom";

import { usePaymentReceiptListByProject } from "../../api/payment";
import { useProjectListWithTxn } from "../../api/project";
import { ProjectAvatar } from "../../common/components";
import { Loader } from "../../common/components/core";
import { PaymentReceiptState } from "../../types/payment";
import { ProjectStateWithTxn } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { getNameInitial } from "../../utils/common";
import ProjectContext from "../project/ProjectContext";

import ProjectAccountDetail from "./components/ProjectAccountDetail";
import ReceiptList from "./components/ReceiptList";

const ProjectAccountView = (): JSX.Element => {
    document.title = PageTitle.ProjectAccountView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const { data: receipts, isValidating } = usePaymentReceiptListByProject(
        project.referenceId,
    );
    const { data: projects, isValidating: isProjectLoading } =
        useProjectListWithTxn();
    const history = useHistory();
    const projectWithTxn = projects?.find(
        (it) => it.state.referenceId === project.referenceId,
    );

    useEffect(() => {
        if (!isProjectLoading && !projectWithTxn) {
            history.push("/not-found");
        }
    }, [history, projectWithTxn, isProjectLoading]);

    return (
        <Loader isLoading={isValidating || isProjectLoading}>
            {receipts && projectWithTxn && (
                <PureProjectAccountView
                    project={projectWithTxn}
                    receipts={receipts}
                />
            )}
        </Loader>
    );
};

type PureProjectAccountViewProps = {
    project: ProjectStateWithTxn;
    receipts: PaymentReceiptState[];
};

export const PureProjectAccountView = ({
    project,
    receipts,
}: PureProjectAccountViewProps): JSX.Element => {
    const history = useHistory();

    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/collection">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    {project.state.name}
                </Breadcrumb.Item>
            </Breadcrumb>

            <div className="d-flex align-items-center mb-5">
                <ProjectAvatar>
                    {getNameInitial(project.state.name)}
                </ProjectAvatar>
                <h1 className="flex-grow-1 mb-0 ms-3">
                    {project.state.name}{" "}
                    <span className="fs-6 fw-bold">
                        #{project.state.referenceId}
                    </span>
                </h1>
            </div>

            <ProjectAccountDetail project={project} />

            <h2 className="mb-3">Payment Transactions</h2>
            <ReceiptList
                receipts={receipts}
                onReceiptClick={(receipt) =>
                    history.push(
                        `/collection/project/${project.state.referenceId}/receipt/${receipt.referenceId}`,
                    )
                }
            />
        </>
    );
};

export default ProjectAccountView;
